import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { PlatformStats } from '@/types';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getPlatformStats();
        if (response.data.success) {
          setStats(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sage"></div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Organizations',
      value: formatNumber(stats?.organizations.total || 0),
      subtext: `${stats?.organizations.active || 0} active`,
      icon: BuildingOfficeIcon,
      accentColor: 'bg-sage',
      href: '/admin/organizations',
    },
    {
      name: 'Affiliates',
      value: formatNumber(stats?.affiliates.total || 0),
      subtext: `${stats?.affiliates.pending || 0} pending`,
      icon: UserGroupIcon,
      accentColor: 'bg-rust',
      href: '/admin/users',
    },
    {
      name: 'Total Commissions',
      value: formatCurrency(stats?.commissions.totalAmount || 0),
      subtext: `${stats?.commissions.total || 0} total`,
      icon: CurrencyDollarIcon,
      accentColor: 'bg-tan',
    },
    {
      name: 'Total Payouts',
      value: formatCurrency(stats?.payouts.totalAmount || 0),
      subtext: `${stats?.payouts.completed || 0} completed`,
      icon: BanknotesIcon,
      accentColor: 'bg-charcoal',
    },
    {
      name: 'Sales Volume',
      value: formatCurrency(stats?.salesVolume.total || 0),
      subtext: 'All time',
      icon: ChartBarIcon,
      accentColor: 'bg-sage',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-charcoal">Platform Overview</h1>
          <p className="text-charcoal/60">
            Monitor your affiliate platform performance across all organizations.
          </p>
        </div>
      </div>

      {/* Stats Grid with accent bars */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statCards.map((stat) => {
          const content = (
            <>
              {/* Left accent bar */}
              <div className={`absolute top-0 left-0 w-1 h-full ${stat.accentColor}`} />
              <div className="pl-2">
                <p className="text-sm text-charcoal/60 mb-1">{stat.name}</p>
                <p className="font-serif text-2xl text-charcoal leading-none mb-1">{stat.value}</p>
                <p className="text-xs text-charcoal/50">{stat.subtext}</p>
              </div>
            </>
          );

          return stat.href ? (
            <Link
              key={stat.name}
              to={stat.href}
              className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden block hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              {content}
            </Link>
          ) : (
            <div
              key={stat.name}
              className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden"
            >
              {content}
            </div>
          );
        })}
      </div>

      {/* Commission & Payout Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20">
            <h3 className="font-display text-lg font-bold text-charcoal">Commission Status</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-tan/10">
              <span className="text-charcoal/70">Pending</span>
              <span className="font-serif text-lg text-tan-dark">
                {formatCurrency(stats?.commissions.pendingAmount || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-tan/10">
              <span className="text-charcoal/70">Approved (Awaiting Payout)</span>
              <span className="font-serif text-lg text-rust">
                {formatNumber(stats?.commissions.approved || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-charcoal/70">Paid</span>
              <span className="font-serif text-lg text-sage">
                {formatNumber(stats?.commissions.paid || 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20">
            <h3 className="font-display text-lg font-bold text-charcoal">Payout Status</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-tan/10">
              <span className="text-charcoal/70">Pending</span>
              <span className="font-serif text-lg text-tan-dark">
                {formatNumber(stats?.payouts.pending || 0)} payouts
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-tan/10">
              <span className="text-charcoal/70">Completed</span>
              <span className="font-serif text-lg text-sage">
                {formatNumber(stats?.payouts.completed || 0)} payouts
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-charcoal/70">Total Paid</span>
              <span className="font-serif text-lg text-charcoal">
                {formatCurrency(stats?.payouts.totalAmount || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/20">
          <h3 className="font-display text-lg font-bold text-charcoal">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/admin/organizations"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="w-5 h-5 text-rust" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Manage Organizations</p>
                <p className="text-sm text-charcoal/60">View and manage all organizations</p>
              </div>
            </Link>

            <Link
              to="/admin/users"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Manage Users</p>
                <p className="text-sm text-charcoal/60">View all platform users</p>
              </div>
            </Link>

            <Link
              to="/admin/settings"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-charcoal/10 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-5 h-5 text-charcoal" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Platform Settings</p>
                <p className="text-sm text-charcoal/60">Configure platform defaults</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
